import {BasePopupProps} from "../../../../utils/Models";
import {OrderEditSaveSummaryModel} from "../../order.models";
import {ReactElement, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {toString as kendoToString} from "@progress/kendo-intl"
import {parseISO} from "date-fns";
import * as icons from "@fortawesome/free-solid-svg-icons"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import FlintLabelBox from "../../../../components/controls/FlintLabelBox";
import FlintButton from "../../../../components/controls/FlintButton";
import {VivoPortal} from "../../../../utils/AppConstants";
import EnumRedirectAfterSave = VivoPortal.EnumRedirectAfterSave;

export default function OrderEditSaveConfirmationPanel(props: OrderEditSaveConfirmationPanelProps) {
    const {state, utils, handlers} = useOrderEditSaveConfirmationPanel(props);
    const {t} = useTranslation();
    return <div className={"order-edit-save-confirmation-panel"}>
        {state.headerInfo.get && <div className={"alert alert-success"}>{state.headerInfo.get}</div>}
        <div className="row">
            <div className="col-12">
                <div className="row mb-2">
                    <div className="col-6">
                        <div className="text-bold">{t("ORDER_DATE_REQUESTED")}:</div>
                        <div className="text-muted">({utils.localTimeZoneName})</div>
                        <div className="text-bold mb-2 font-size-lg">{state.requestedDate.get}</div>
                    </div>
                    <div className="col-6">
                        <div className="text-bold">Applied requested date:</div>
                        <div className="text-muted">({utils.localTimeZoneName})</div>
                        <div className="text-bold color-flint-red mb-2 font-size-lg">{state.dateAssigned.get}</div>
                    </div>
                </div>
                <span className="badge f-bg-in-progress-bg color-body font-size-larger fw-normal w-100 py-3">Applied requested date may differ from the date requested by the user because of the<br/>following reasons:</span>
                <div className={"d-flex mt-2 mb-3 justify-content-around"}>
                    <div><FontAwesomeIcon icon={icons.faCheckCircle} className="me-2 color-warning"/>company minimum
                        leadtime
                    </div>
                    <div><FontAwesomeIcon icon={icons.faCheckCircle} className="me-2 color-warning"/>company daily job
                        limit
                    </div>
                    <div><FontAwesomeIcon icon={icons.faCheckCircle} className="me-2 color-warning"/>laboratory work
                        schedule
                    </div>
                </div>
            </div>
        </div>
        <div className="row mb-1">
            <div className="col-12">
                <div className="text-bold">Company leadtime: {state.model.get.CompanyLeadTime} hour(s)</div>
                {state.model.get.JobAssignmentData &&
                    <div className="row" ng-if="ctrl.OSI.CompanyJobLimit">
                        <div className="col-12">
                            <div>Order company has a daily job limit = <b>{state.model.get.CompanyJobNumber}</b></div>
                            <div className="font-weight-semibold my-1">Order contains <span
                                className="badge bg-info text-body mx-1">{state.model.get.JobAssignmentData.OrderJobsNo}</span> jobs
                                {state.model.get.JobAssignmentData.OrderJobsNo != state.model.get.JobAssignmentData.OrderRecalculatedJobs &&
                                    <span className={"ms-2"}
                                          ng-if="ctrl.OSI.OrderJobsNo != ctrl.OSI.RecalculatedOrderJobsNo">
                                    (<span
                                        className="badge bg-danger me-1">{state.model.get.JobAssignmentData.OrderRecalculatedJobs}</span>after applying FP factor)
                                </span>}
                            </div>
                            <div>According to the limit and the amount of order jobs, processing has been assigned to
                                the following dates:
                            </div>
                            <div className="row text-bold border-bottom-1">
                                <div className="col-2">Date</div>
                                <div className="col-2 text-end">Jobs</div>
                            </div>
                            <div className="row text-bold">
                                <div className={"col-4 border-bottom border-bottom-2 pb-1 mb-1"}/>
                            </div>
                            <div className={"container"} style={{height: "75px", overflow: "auto"}}>
                                {state.model.get.JobAssignmentData.OrderJobs.map((job) => {
                                    return (
                                        <div key={job.Id} className="row">
                                            <div className="col-4">
                                                <FlintLabelBox
                                                    className={"d-flex justify-content-between"}
                                                    title={utils.getLocalDateAsString(job.LabDate, "yyyy-MM-dd")}
                                                    text={job.JobsAssignedNo}/>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                            <div className="row text-bold">
                                <div className={"col-4 border-top border-top-2 pt-1 mt-1"}/>
                            </div>
                            <div className="row text-bold">
                                <div className="col-2">Total</div>
                                <div
                                    className="col-2 text-end">{state.model.get.JobAssignmentData.OrderRecalculatedJobs}</div>
                            </div>
                        </div>
                    </div>}
            </div>
        </div>
        <div className={"form-group btn-section text-center gap-3 d-flex justify-content-center"}>
            <FlintButton style={{minWidth: "150px"}} themeColor={"secondary"} onClick={handlers.handleRedirectToPrint}
                         className={"me-2"}>
                <FontAwesomeIcon icon={icons.faPrint} className={"me-2"}/>
                {t("Print")}
            </FlintButton>
            <FlintButton style={{minWidth: "150px"}} onClick={handlers.handleRedirectToOrderList}>
                <FontAwesomeIcon icon={icons.faList} className={"me-2"}/>
                {t("NAV_OrderList")}
            </FlintButton>
        </div>
    </div>
}

export interface OrderEditSaveConfirmationPanelProps extends BasePopupProps<EnumRedirectAfterSave> {
    saveOrderSummary: OrderEditSaveSummaryModel
}

function useOrderEditSaveConfirmationPanel(props: OrderEditSaveConfirmationPanelProps) {
    const [headerInfo, setHeaderInfo] = useState<ReactElement>();
    const localTimeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
    useEffect(() => {
        const dateRequested = new Date(props.saveOrderSummary.DateRequested);
        const dateAssigned = new Date(props.saveOrderSummary.DateAssigned);
        if (dateRequested.getTime() != dateAssigned.getTime()) {
            setHeaderInfo(<div className='h5 text-danger text-center'>Please notice, that the applied requested
                date<br/>differs from originally requested one.</div>);
            return;
        }
        setHeaderInfo(undefined);
    }, [props.saveOrderSummary])

    function getLocalDateAsString(date: Date, format: string = "yyyy-MM-dd HH:mm"): string {
        return kendoToString(parseISO(date.toString() + 'Z'), format)
    }
    function handleRedirectToPrint() {
        props.OnConfirmedAndSaved(EnumRedirectAfterSave.RedirectToPrintPage);        
    }

    function handleRedirectToOrderList() {
        props.OnConfirmedAndSaved(EnumRedirectAfterSave.RedirectToOrderListPage);
    }

    return {
        state: {
            headerInfo: {get: headerInfo},
            requestedDate: {get: getLocalDateAsString(props.saveOrderSummary.DateRequested)},
            dateAssigned: {get: getLocalDateAsString(props.saveOrderSummary.DateAssigned)},
            model: {get: props.saveOrderSummary}
        },
        utils: {
            localTimeZoneName,
            getLocalDateAsString,
        },
        handlers: {
            handleRedirectToOrderList,
            handleRedirectToPrint,
        }
    }
}